import { drawing as d, geometry as g } from '@progress/kendo-drawing';
import { START } from '../constants';
import { MarkerBase } from './MarkerBase';
import { lineAngle } from './utils';
import { deepExtend } from './../utils/deep-extend';
/**
 * An object that defines the default options for the arrow marker.
 */
export const defaultArrowOptions = {
    path: 'M 0 0 L 10 5 L 0 10 L 3 5 z',
    anchor: {
        x: 10,
        y: 5
    }
};
/**
 * An object that defines the line arrow options for the arrow marker.
 */
export const lineArrowOptions = {
    path: 'M 0 0 L 8 7 L 0 14',
    anchor: {
        x: 8,
        y: 7
    },
    fill: null,
    stroke: {
        color: '#000',
        lineCap: 'round',
        lineJoin: 'round',
        width: 1
    }
};
/**
 * An object that defines the triangle arrow options for the arrow marker.
 */
export const triangleArrowOptions = {
    path: 'M 0 0 L 8.5 5 L 0 10 L 0 0 z',
    anchor: {
        x: 8.5,
        y: 5
    }
};
/**
 * A visual marker that renders an arrow shape at the start or end of a path.
 * Extends MarkerBase to provide arrow-specific positioning and rendering functionality.
 */
export class ArrowMarker extends MarkerBase {
    /**
     * Creates a new ArrowMarker instance.
     * @param options Configuration options for the arrow marker
     */
    constructor(options) {
        options = deepExtend({}, defaultArrowOptions, options);
        super(options);
    }
    /**
     * Creates the drawing element for the arrow marker.
     */
    createElement() {
        const options = this.options;
        this.drawingElement = d.Path.parse(options.path, {
            fill: options.fill,
            stroke: options.stroke
        });
    }
    /**
     * Positions the arrow marker on the specified path.
     * @param path The path on which to position the arrow marker
     */
    positionMarker(path) {
        const points = this._linePoints(path);
        const start = points.start;
        const end = points.end;
        const transform = g.transform();
        if (start) {
            transform.rotate(lineAngle(start, end), end);
        }
        if (end) {
            const anchor = this.anchor;
            const translate = end.clone().translate(-anchor.x, -anchor.y);
            transform.translate(translate.x, translate.y);
        }
        this.drawingElement.transform(transform);
    }
    /** @hidden */
    _linePoints(path) {
        const options = this.options;
        const segments = path.segments;
        let startPoint, endPoint, targetSegment;
        if (options.position === START) {
            targetSegment = segments[0];
            if (targetSegment) {
                endPoint = targetSegment.anchor();
                startPoint = targetSegment.controlOut();
                const nextSegment = segments[1];
                if (!startPoint && nextSegment) {
                    startPoint = nextSegment.anchor();
                }
            }
        }
        else {
            targetSegment = segments[segments.length - 1];
            if (targetSegment) {
                endPoint = targetSegment.anchor();
                startPoint = targetSegment.controlIn();
                const prevSegment = segments[segments.length - 2];
                if (!startPoint && prevSegment) {
                    startPoint = prevSegment.anchor();
                }
            }
        }
        if (endPoint) {
            return {
                start: this._transformToPath(startPoint, path),
                end: this._transformToPath(endPoint, path)
            };
        }
    }
}
